import { use, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Routes from 'shared/enums/routes';
import AuthProviderContext from 'shared/features/auth/authentication/providers/contexts/auth-provider-context';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import MenuItem, { MenuItemGroup } from 'features/menu/types/menu-item';

type Menu = {
  goToRoute: (route: string) => void;
  isActive: (route: string) => boolean;
  authorizedMenuItems: (items: MenuItemGroup['menuItems']) => MenuItemGroup['menuItems'];
  showDivider: (index: number, menuLength: number, items: MenuItem[]) => boolean;
};

const useMenu = (): Menu => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRoutePermission } = usePermissionAuthorization();
  const {
    tenant: { switchActivated },
  } = use(AuthProviderContext);

  const goToRoute = (route: string): void => {
    navigate(route);
  };

  const isActive = useCallback(
    (route: string) => {
      const splittedCurrentRoute = location.pathname.split('/');
      const splittedMenuRoute = route.split('/');

      if (splittedCurrentRoute.length >= 2 && splittedMenuRoute.length >= 2) {
        return splittedCurrentRoute[1] === splittedMenuRoute[1];
      }

      return false;
    },
    [location.pathname],
  );

  const filterOutTenantMenuItemWhenTenantSwitchIsActivated = (item: MenuItemGroup['menuItems'][number]): boolean =>
    !(switchActivated && item.route === Routes.Tenants);

  const authorizedMenuItems = (items: MenuItemGroup['menuItems']): MenuItem[] =>
    items.filter((item) => filterOutTenantMenuItemWhenTenantSwitchIsActivated(item) && hasRoutePermission(item.route));

  const showDivider = (index: number, menuLength: number, items: MenuItemGroup['menuItems']): boolean =>
    index < menuLength - 1 && authorizedMenuItems(items).length > 0;

  return {
    goToRoute,
    isActive,
    authorizedMenuItems,
    showDivider,
  };
};

export default useMenu;
